/**
 * Where the signed-in session lives.
 *
 * In `localStorage`, so a sign-in lasts: a new tab, a link from nsibi.app
 * and coming back tomorrow are all signed in, until Sign out or the 30-day
 * token lapses (it is refreshed while the app is used, refreshSession.ts) -
 * how WhatsApp Web and TikTok behave. It was `sessionStorage` until
 * 2026-10-21, gone with the tab, on the argument that a token dying with
 * the tab is a smaller window on a shared machine; in practice it asked a
 * person to sign in again for every tab, including the one the public
 * site's "Log in" opens (the owner's call: stay signed in). The trade-off
 * stands: on a shared computer the next person is signed in unless Sign
 * out was pressed.
 *
 * The token is still readable by any script on this origin, as it was. The
 * real fix is unchanged - an httpOnly refresh cookie, or a short-lived
 * access token minted against one - and is not built.
 *
 * Every tab reads the one stored session, and a change in one (sign-in,
 * a refreshed token, Sign out) reaches the others through the `storage`
 * event (`onSessionChange`). Alongside it a HINT cookie, shared with
 * nsibi.app, says only that this browser is signed in (never the token):
 * the public site reads it to send a signed-in reader straight here
 * instead of asking them to log in (web-site SignInPrompt).
 */
const KEY = 'nsibi.session.v1';

export type Session = {
  token: string;
  tenantSlug: string;
  user: {
    id: number;
    display_name: string;
    avatar_url: string;
    external_user_id: string;
  };
  /**
   * What a brand-new account still owes before the app opens: a name, and a
   * date of birth when the server has none.
   *
   * Set by an EMAIL sign-in only (`lib/auth.ts`); a session linked by QR never
   * has it, because that account already exists on a phone and was set up
   * there. It lives on the stored session, not in React state, so closing the
   * tab half way through the form puts the person back on the form and not in
   * a chat list under no name. Absent means nothing is owed.
   */
  onboarding?: { name: boolean; dob: boolean };
};

let cached: Session | null | undefined;

function parse(raw: string | null): Session | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as Session;
  } catch {
    return null;
  }
}

export function getSession(): Session | null {
  if (cached !== undefined) return cached;
  try {
    let raw = localStorage.getItem(KEY);
    if (!raw) {
      // A tab signed in before sessions moved to localStorage: carried
      // over once, so nobody is signed out by the change.
      raw = sessionStorage.getItem(KEY);
      if (raw) {
        localStorage.setItem(KEY, raw);
        sessionStorage.removeItem(KEY);
      }
    }
    cached = parse(raw);
  } catch {
    // Private mode, or storage disabled. Treat as signed out rather than
    // crashing the whole app on a storage exception.
    cached = null;
  }
  // The hint follows the session both ways: a session gone from storage
  // (cleared site data, another profile's sign-out) must not leave nsibi.app
  // believing this browser is signed in - it sends a signed-in browser
  // straight here (web-site `WebAppRedirect`), and a stale hint would bounce
  // a signed-out reader to the sign-in page on every watch page.
  markSignedIn(cached !== null);
  return cached;
}

export function setSession(session: Session): void {
  cached = session;
  try {
    localStorage.setItem(KEY, JSON.stringify(session));
  } catch {
    /* in-memory only; the session still works for this page load */
  }
  markSignedIn(true);
}

export function clearSession(): void {
  cached = null;
  try {
    localStorage.removeItem(KEY);
    sessionStorage.removeItem(KEY);
  } catch {
    /* nothing to do */
  }
  markSignedIn(false);
}

/**
 * Another tab signed in, refreshed the token or signed out. `fn` gets the
 * session as it now is. (A tab never hears its own writes - the `storage`
 * event fires only in the others.)
 */
export function onSessionChange(fn: (session: Session | null) => void): () => void {
  const handler = (event: StorageEvent) => {
    if (event.key !== KEY && event.key !== null) return;
    cached = parse(event.newValue);
    fn(cached);
  };
  window.addEventListener('storage', handler);
  return () => window.removeEventListener('storage', handler);
}

/* ── the hint the public site reads ──────────────────────────────────── */

/** Read by web-site `lib/webSession.ts`: the two must agree on the name. */
const HINT = 'nsibi_web_signed_in';

/**
 * The parent domain, so nsibi.app can read it: `web.nsibi.app` -> `nsibi.app`.
 * Host-only on localhost and LAN names, where cookies are shared across
 * ports anyway (the dev site on :3001 reads the dev app's on :3002).
 */
function hintDomain(host: string): string | null {
  const parts = host.split('.');
  if (parts.length < 3 || /^\d+$/.test(parts[parts.length - 1]) || host.endsWith('.local')) return null;
  return parts.slice(-2).join('.');
}

function markSignedIn(on: boolean): void {
  if (typeof document === 'undefined') return;
  const domain = hintDomain(window.location.hostname);
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  const scope = `; Path=/${domain ? `; Domain=${domain}` : ''}; SameSite=Lax${secure}`;
  document.cookie = on
    ? `${HINT}=1; Max-Age=${30 * 24 * 60 * 60}${scope}`
    : `${HINT}=; Max-Age=0${scope}`;
}
